/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, ProductList, OfferList } from "components";
import { dupeOffer, dupeProduct, getAdBlockById, getCircularById, getImage, importOffers, importProducts, rejectImageMatchAPI, setDupe, moveBlockToReviewQueue, getNoLargeSaveOfferCategories } from "api";
import styled from "styled-components";
import { useLocation } from "@reach/router";
import { useMutation, useQuery } from "react-query";
import { format } from "date-fns";
import {
  Button,
  Col,
  Divider,
  Image,
  Modal,
  Row,
  Tag,
  Tooltip,
  Radio,
  Space,
  Typography
} from "antd";
import { getCachedImage, getIncentiveInfo, setCachedImage, validateOffer, validateProduct, hasCouponAndRebate, COUPON_REBATE_CONFLICT_MESSAGE } from "utils";
import { toast } from "react-toastify";
import { imageFallback } from "config/image";
import { IncentiveList } from "components/common/IncentiveListCommon";

const KEY_MAP = {
  anomaly_f: "Anomaly",
  bulkqty_f: "Bulk Quantity",
  newitem_f: "New Item",
  select_varieties_f: "Selected Varieties",
  bonus_card_f: "Bonus Card",
  brand_equity_f: "Brand Equity",
  coupon_f: "Coupon",
  rebate_f: "Rebate",
  // inc_discount_f: "Inc Discount",
  limitedtime_f: "Limited Time",
  loyalty_f: "Loyalty",
  meal_deal_f: "Meal Deal",
  online_only_f: "Online Only",
  show_math_f: "Show Math",
  sugar_tax_f: "Sugar Tax",
  split: "Split",
  sponsored_f: "Sponsored",
  qr_code_f: "QR Code",
  sold_out_f : "Sold Out"
};

export const ImageMatchingModal = ({
  open,
  currentAdBlockData = '',
  ResetViewOnClose = false,
  circularName = '',
  dupe_block = '',
  onClose,
  view = 'product',
  startTime = '',
  sizeData = [],
  onImport = () => { },
  onError = () => { },
  onSuccess = () => { },
  onSuccessDupeAdBlock = null,
}) => {
  const location = useLocation();
  const [confirmDupe, setConfirmDupe] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false);
  const [selectedView, setSelectedView] = useState(view);
  const [similarCircularName, setSimilarCircularName] = useState('');
  const [productData, setProductData] = useState([]);
  const [offersData, setOffersData] = useState([]);
  const [incentiveData, setIncentiveData] = useState([]);
  const [importOnly, setImportOnly] = useState(false);
  const [currentBlockLinerMismatch, setCurrentBlockLinerMismatch] = useState(false);

  // Ensure the modal view mirrors the latest tab selection from the parent when it changes.
  useEffect(() => {
    setSelectedView(view);
  }, [view]);

  useEffect(() => {
    if (!open) return;
    setConfirmDupe(false);
    setConfirmReview(false);
  }, [open]);

  // While the modal is open, swallow keyboard events at the window capture
  // phase so the background screen's global shortcuts (registered on window
  // via react-use-keypress, e.g. Alt+Q approve) cannot fire. Without this,
  // users could approve in the background and then click Complete Match,
  // double-counting the same block in the counter statistics.
  // Escape is allowed through so the modal itself can still be cancelled.
  useEffect(() => {
    if (!open) return;
    const blockBackgroundShortcuts = (event) => {
      if (event.key === "Escape") return;
      event.stopPropagation();
    };
    window.addEventListener("keydown", blockBackgroundShortcuts, true);
    window.addEventListener("keyup", blockBackgroundShortcuts, true);
    return () => {
      window.removeEventListener("keydown", blockBackgroundShortcuts, true);
      window.removeEventListener("keyup", blockBackgroundShortcuts, true);
    };
  }, [open]);

  const closeModal = () => {
    if (ResetViewOnClose) {
      setSelectedView('product');
    }
    onClose();
  };

  const { data: { payload: currentImage } = {}, isFetching: isCurrentImageLoading } = useQuery(
    ['currentImage', currentAdBlockData.filename],
    async () => loadImage(currentAdBlockData.filename),
    {
      enabled: (Boolean(dupe_block) && !currentAdBlockData.dupe_of) && ((view === 'product' && (!currentAdBlockData.products_approved && !currentAdBlockData.product_dupe_of)) || (view === 'offer' && (!currentAdBlockData.offers_approved && !currentAdBlockData.offer_dupe_of))),
      initialData: { payload: [] },
      refetchOnWindowFocus: false, // Disable refetching on window focus
      refetchOnMount: false, // Disable refetching on component mount,
    }
  );

  const { data: { payload: similarAdBlockData }, isFetching: isSimilarAdBlockLoading } = useQuery(
    ['similarAdBlockData', dupe_block],
    async () => getAdBlockById(dupe_block),
    {
      enabled: (Boolean(dupe_block) && !currentAdBlockData.dupe_of) && ((view === 'product' && (!currentAdBlockData.products_approved && !currentAdBlockData.product_dupe_of)) || (view === 'offer' && (!currentAdBlockData.offers_approved && !currentAdBlockData.offer_dupe_of))),
      initialData: { payload: [] },
      refetchOnWindowFocus: false, // Disable refetching on window focus
      refetchOnMount: false, // Disable refetching on component mount
      onSuccess: async (data) => { await onLoad(data.payload); }
    })

  const { data: { payload: similarImage } } = useQuery(
    ['similarImage', similarAdBlockData?.filename],
    async () => loadImage(similarAdBlockData?.filename),
    {
      enabled: similarAdBlockData?.filename ? true : false,
      initialData: { payload: [] },
      // refetchOnWindowFocus: false, // Disable refetching on window focus
      // refetchOnMount: false, // Disable refetching on component mount
    }
  )

  const onLoad = async (data) => {
    if (!data) return;
    const result = await getCircularById(data.circular_uuid);
    if (result.success) {
      setSimilarCircularName(
        result.payload.circ_subtype +
        "_" +
        result.payload.retailer.name +
        "_" +
        format(
          new Date(
            result.payload.circ_week_dt.replace(/-/g, "/").replace(/T.+/, "")
          ),
          "MM/dd/yyyy"
        ) +
        `_${result.payload.ext_id}` +
        `_${result.payload.circ_version ? result.payload.circ_version : 0}`
      );
    } else {
      //TODO: set error message about no circular matching
    }
    const products = Array.isArray(data?.products) ? data.products : [];
    const offers = Array.isArray(data?.offers) ? data.offers : [];

    const validatedProducts = products.map((product) => ({
      ...product,
      isValid: validateProduct(product, sizeData)
    }));
    const validatedOffers = offers.map((offer) => ({
      ...offer,
      isValid: validateOffer(offer)
    }));

    const validateLargeSaveOfferCategoriesResponse = await getNoLargeSaveOfferCategories();
    if (validateLargeSaveOfferCategoriesResponse?.success) {
      const largeSaveCategories  = validateLargeSaveOfferCategoriesResponse.results.map(value => value.sub_cat_i);

      const hasLargeSaveCategoryLiner = products.some((product) => {
        const category = product?.brand?.sub_cat_i;
        return category && largeSaveCategories.includes(category) && Boolean(product?.liner_f);
      });

      const hasLargeSaveCategoryNonLiner = products.some((product) => {
        const category = product?.brand?.sub_cat_i;
        return category && largeSaveCategories.includes(category) && !Boolean(product?.liner_f);
      });

      if (hasLargeSaveCategoryLiner || hasLargeSaveCategoryNonLiner) {
        validatedOffers.forEach((offer) => {
          const isPrimaryOffer = String(offer?.dd_index) === "1";
          const exceedsValueLimit = Number(offer?.value_1) > 50 || Number(offer?.value_2) > 50;

          if (!isPrimaryOffer || !exceedsValueLimit) return;

          if (hasLargeSaveCategoryLiner && Boolean(offer?.liner_f)) {
            offer.isValid = false;
          }

          if (hasLargeSaveCategoryNonLiner && !Boolean(offer?.liner_f)) {
            offer.isValid = false;
          }
        });
      }
    }

    const hasInvalidProduct = validatedProducts.some((product) => !product.isValid);
    const hasInvalidOffer = validatedOffers.some((offer) => !offer.isValid);

    const toastOptions = {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      progress: undefined,
      theme: "dark"
    };

    const notifyImportOnly = (message) => {
      toast.error(`${message} Only import is permitted for this block.`, toastOptions);
    };

    if (hasInvalidProduct) {
      notifyImportOnly("Some products in the matched block contain invalid data.");
    }

    if (hasInvalidOffer) {
      notifyImportOnly("Some offers in the matched block contain invalid data.");
    }

    setProductData(validatedProducts);
    setOffersData(validatedOffers);
    let incentivesData = [];
    if (data?.incentive_text) {
      incentivesData = getIncentiveInfo(data?.incentive_text).map((incentives, index) => ({
        incentive_type: incentives.incentive_type,
        incentive_text: incentives.incentive_text,
        index, // Use the index from map instead of manually calculating it
        isValid: true,
        incentive_attach_to: 'Adblock',
        incentive_limit: incentives.limit,
        incentive_include_net_cost: incentives.net_cost,
        incentive_by_mail: incentives.by_mail,
        incentive_online_incentive: incentives.online_incentive,
        incentive_liner_f: Boolean(incentives.incentive_liner_f)
      }));
    }
    setIncentiveData(incentivesData);
    

    // Liner Validation Starts
    const hasProductLiner = validatedProducts.some(item => item?.liner_f === true);
    const hasProductNonLiner = validatedProducts.some(item => item?.liner_f === false);
    let linerMismatch = false;
    const hasOfferLiner = validatedOffers.some(item => item?.liner_f === true);
    const hasOfferNonLiner = validatedOffers.some(item => item?.liner_f === false);
    const hasIncentiveLiner = incentivesData.some(item => item?.incentive_liner_f === true);
    const hasIncentiveNonLiner = incentivesData.some(item => item?.incentive_liner_f === false);
    if (hasProductLiner && hasProductNonLiner) {
      const hasAnyLiner = hasOfferLiner || hasIncentiveLiner;
      const hasAnyNonLiner = hasOfferNonLiner || hasIncentiveNonLiner;
      const isMixed = hasAnyLiner && hasAnyNonLiner;
      if (!isMixed) {
        linerMismatch = !hasAnyLiner || !hasAnyNonLiner;
      }
    } else if (hasProductLiner) {
      linerMismatch = !(validatedOffers.every(o => o?.liner_f === true) && incentivesData.every(i => i?.incentive_liner_f === true));
    } else if (hasProductNonLiner) {
      linerMismatch = hasOfferLiner || hasIncentiveLiner;
    }
    console.log("Liner Mismatch:", linerMismatch);
    // Liner Validation Ends

    // Cross-block Liner Validation: current block's products vs matched block's offers
    const currentProducts = Array.isArray(currentAdBlockData?.products) ? currentAdBlockData.products : [];
    const hasCurrentProductLiner = currentProducts.some(item => item?.liner_f === true);
    const hasCurrentProductNonLiner = currentProducts.some(item => item?.liner_f === false);
    let crossBlockLinerMismatch = false;
    if (hasCurrentProductLiner && hasCurrentProductNonLiner) {
      const hasAnyOfferLiner = hasOfferLiner || hasIncentiveLiner;
      const hasAnyOfferNonLiner = hasOfferNonLiner || hasIncentiveNonLiner;
      const isMixed = hasAnyOfferLiner && hasAnyOfferNonLiner;
      if (!isMixed) {
        crossBlockLinerMismatch = !hasAnyOfferLiner || !hasAnyOfferNonLiner;
      }
    } else if (hasCurrentProductLiner) {
      crossBlockLinerMismatch = !(validatedOffers.every(o => o?.liner_f === true) && incentivesData.every(i => i?.incentive_liner_f === true));
    } else if (hasCurrentProductNonLiner) {
      crossBlockLinerMismatch = hasOfferLiner || hasIncentiveLiner;
    }
    console.log("Cross-block Liner Mismatch:", crossBlockLinerMismatch);
    if (crossBlockLinerMismatch) {
      notifyImportOnly("Liner mismatch detected between current block and matched block. ");
    }
    setCurrentBlockLinerMismatch(crossBlockLinerMismatch);

    // AD-4468: a block flagged as both Coupon and Rebate must not be duplicated
    // or complete matched; importing is still allowed so the data can be fixed.
    const couponRebateConflict = hasCouponAndRebate(data) || hasCouponAndRebate(currentAdBlockData);
    if (couponRebateConflict) {
      notifyImportOnly(COUPON_REBATE_CONFLICT_MESSAGE);
    }

    const hasInvalidData = hasInvalidProduct || hasInvalidOffer || linerMismatch || couponRebateConflict;
    setImportOnly(hasInvalidData);
  }

  const loadImage = async (filename) => {
    if (!filename) return { payload: [] };
    const cachedImage = await getCachedImage(filename);
    if (cachedImage) {
      return { payload: cachedImage };
    }
    const backImg = await getImage(filename);
    if (backImg?.success) {
      await setCachedImage(filename, backImg.payload);
      return { payload: backImg.payload };
    }
    return { payload: '' };
  };


  const handleImportProduct = useMutation({
    mutationFn: (data) => importProducts(data.adblockId, data.parentId, data.startTime),
    onSuccess: (data) => {
      const products = data.payload
      closeModal();
      onImport(products);
    }
  });

  const handleDupeProduct = useMutation({
    mutationFn: (data) => dupeProduct(data.adblockId, data.parentId, data.startTime),
    onSuccess: () => {
      if (onSuccessDupeAdBlock) {
        onSuccessDupeAdBlock();
      } else {
        onSuccess();
      }
      closeModal();
    },
    onError: (error) => {
      onError(error);
    }
  })

  const handleDupeOffer = useMutation({
    mutationFn: (data) => dupeOffer(data.adblockId, data.parentId, data.startTime),
    onSuccess: () => {
      onSuccess();
      closeModal();
    },
    onError: (error) => {
      onError(error);
    }
  })

  const handleImportOffer = useMutation({
    mutationFn: (data) => importOffers(data.adblockId, data.parentId, data.dupe, data.startTime),
    onSuccess: (data) => {
      closeModal();
      onImport(data);
    }
  });

  const handleSetCompleteDuplicate = useMutation({
    mutationFn: (data) => setDupe(data.adblockId, data.parentId, startTime),
    onSuccess: () => {
      onSuccess();
      closeModal();
    },
  })

  const handleMoveToReviewQueue = useMutation({
    mutationFn: (data) => moveBlockToReviewQueue(data),
    onSuccess: () => {
      onSuccess();
      closeModal();
    }
  });

  const handleCancel = useMutation({
    mutationFn: () => {
      return rejectImageMatchAPI(currentAdBlockData.uuid, view);
    },
    onSuccess: () => {
      toast.info("Image Match Rejected", {
        position: "bottom-center", autoClose: 1000, hideProgressBar: false, closeOnClick: true,
        pauseOnHover: true, progress: undefined, theme: "dark"
      });
    },
    onError: (error) => {
      toast.error("Error Rejecting Image Match", {
        position: "bottom-center", autoClose: 1000, hideProgressBar: false, closeOnClick: true,
        pauseOnHover: true, progress: undefined, theme: "dark"
      });
    },
    onSettled: () => {
      closeModal();
    }
  });

  const isLoading = handleImportOffer.isLoading || handleImportProduct.isLoading || handleSetCompleteDuplicate.isLoading || handleDupeProduct.isLoading || handleDupeOffer.isLoading || handleCancel.isLoading || isCurrentImageLoading || isSimilarAdBlockLoading;

  return (
    <Modal
      open={open}
      onCancel={() => {
          handleCancel.mutate();
      }}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Typography.Title level={4}>
              Similar Block Identification
            </Typography.Title>
          </Col>
          <Col>
            <StyledRadioGroup
              value={selectedView}
              onChange={(e) => setSelectedView(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="product">Products</Radio.Button>
              <Radio.Button value="offer">Offers</Radio.Button>
            </StyledRadioGroup>
          </Col>
        </Row>
      }
      centered
      width={'90%'}
      styles={{ body : {overflowX: 'hidden'} }}
      footer={<Row justify="center" style={{ marginTop: 24 }}>
        <Space size="middle">
          <Button
            danger
            disabled={isLoading}
            onClick={() => handleCancel.mutate()}
          >
            Cancel
          </Button>

          {selectedView === 'product' ? (
            <>
              <Button
                disabled={isLoading}
                onClick={() => handleImportProduct.mutate({
                  adblockId: currentAdBlockData.uuid,
                  parentId: similarAdBlockData.uuid,
                  startTime: startTime
                })}
              >
                Import Products (EM)
              </Button>
              <Button
                disabled={isLoading || handleCancel.has_children || importOnly || !similarAdBlockData.products_approved}
                onClick={() => handleDupeProduct.mutate({
                  adblockId: currentAdBlockData.uuid,
                  parentId: similarAdBlockData.uuid,
                  startTime: startTime
                })}
              >
                Duplicate Products (IM)
              </Button>
            </>
          ) : (
            <>
              <Button
                disabled={isLoading || view !== 'offer'}
                onClick={() => handleImportOffer.mutate({
                  adblockId: currentAdBlockData.uuid,
                  parentId: similarAdBlockData.uuid,
                  startTime: startTime
                })}
              >
                Import Offers (EM)
              </Button>
              <Button
                disabled={isLoading || handleCancel.has_children || currentAdBlockData?.product_dupe_of || !currentAdBlockData?.products_approved || importOnly || !similarAdBlockData.offers_approved || currentBlockLinerMismatch}
                onClick={() => handleDupeOffer.mutate({
                  adblockId: currentAdBlockData.uuid,
                  parentId: similarAdBlockData.uuid,
                  startTime: startTime
                })}
              >
                Duplicate Offers (IM)
              </Button>
            </>
          )}

          <CompleteMatchButton
            disabled={isLoading || importOnly}
            onClick={() => {
              if (confirmDupe) {
                handleSetCompleteDuplicate.mutate({
                  adblockId: currentAdBlockData.uuid,
                  parentId: similarAdBlockData.uuid
                })
              } else {
                setConfirmDupe(true);
              }
            }}
          >
            {!confirmDupe ? 'Complete Duplicate (CM)' : 'Confirm?'}
          </CompleteMatchButton>

          <ReviewQueueButton
            disabled={isLoading}
            onClick={() => {
              if (confirmReview) {
                handleMoveToReviewQueue.mutate({
                  adblock_id: currentAdBlockData.id,
                  parent_adblock_id: similarAdBlockData.id
                })
              } else {
                setConfirmReview(true);
              }
            }}
          >
            {!confirmReview ? 'Move to Review Queue' : 'Confirm?'}
          </ReviewQueueButton>
        </Space>
      </Row>}
      style={{ top: 15 }}
      maskClosable={false}
      closeIcon={null}
      closable={false}
    >
      <Row gutter={24}>
        <Col span={8}>        
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <Divider orientation="center">Matched Block</Divider>
            <Link
              target="_blank"
              href={
                location.origin
                + (
                  location.pathname.includes("/product/") ? "/product/" :
                    location.pathname.includes("/offer/") ? "/offer/" :
                      location.pathname.substring(0, location.pathname.indexOf("/", 1) + 1)
                ) + currentAdBlockData?.similar_circular_uuid
                + "/" + currentAdBlockData?.similar_page_uuid
                + "/" + currentAdBlockData?.similar_adblock_uuid
              }
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div>{similarCircularName ? similarCircularName : 'Loading'}</div>
            </Link>
              <ImageDiv>
                <StyledImage
                  preview={true}
                  src={`data:image/jpeg;base64,${similarImage}`}
                  fallback={imageFallback}
                />
              </ImageDiv>
              <Space size={8}>
                <Tag color={similarAdBlockData?.products_approved ? 'green' : 'yellow'}>
                  {similarAdBlockData?.products_approved ? 'Product Approved' : 'Product Incomplete'}
                </Tag>
                <Tag color={similarAdBlockData?.offers_approved ? 'green' : 'yellow'}>
                  {similarAdBlockData?.offers_approved ? 'Offer Approved' : 'Offer Incomplete'}
                </Tag>
              </Space>
            </Space>
        </Col>
        <Col span={8}>
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <Divider orientation="center">Current Block</Divider>
            <div>{circularName}</div>
            <ImageDiv>
              <StyledImage
                src={`data:image/jpeg;base64,${currentImage}`}
                preview={true}
                fallback={imageFallback}
              />
            </ImageDiv>
            <Space size={8}>
              <Tag color={currentAdBlockData?.products_approved ? 'green' : 'yellow'}>
                {currentAdBlockData?.products_approved ? 'Product Approved' : 'Product Incomplete'}
              </Tag>
              <Tag color={currentAdBlockData?.offers_approved ? 'green' : 'yellow'}>
                {currentAdBlockData?.offers_approved ? 'Offer Approved' : 'Offer Incomplete'}
              </Tag>
              {currentAdBlockData?.has_children && (
                <Tooltip title="This block has children. When marked as Complete Match, all its children will also be marked as CM of the matched block." color="#101010">
                  <Tag color="cyan">Has Children</Tag>
                </Tooltip>
              )}
            </Space>
          </Space>
        </Col>
        <Col span={8}>
          {selectedView === 'product' ? (
            <div>
              <Divider orientation="center">Products in Matched Block</Divider>
              <ProductList
                productsData={productData}
                width="small"
                displayOnly={true}
                sizeData={sizeData}
              />
            </div>
          ) : (
            <div>
              <Divider orientation="center">Offers/Incentives in Matched Block</Divider>
              <OfferList
                offersData={offersData}
                width="small"
                displayOnly={true}
                sizeData={sizeData}
              />
              <IncentiveList
                incentiveData={incentiveData}
                displayOnly={true}
                width="small"
              />
            </div>
          )}
          <Divider orientation="center">Meta Data in Matched Block</Divider>
          <MetadataContainer>
            {Object.keys(KEY_MAP).filter((key) => similarAdBlockData[key]).length === 0 ? (
              <EmptyContentContainer>
                <p>No Meta Data</p>
              </EmptyContentContainer>
            ) : (
              <Space size={8} wrap style={{ display: 'flex', justifyContent: 'center' }}>
                {Object.keys(KEY_MAP)
                  .filter((key) => similarAdBlockData[key])
                  .map((key) => {
                    const value = similarAdBlockData[key];
                    const isEnabled = value === true || value === 1;
                    return isEnabled ? (
                      <Tag key={key}>
                        {KEY_MAP[key]} {KEY_MAP[key] === 'Coupon'
                          ? `: ${similarAdBlockData.coupon_amnt}` : KEY_MAP[key] === 'Rebate'
                            ? `: ${similarAdBlockData.rebate_amnt}` : KEY_MAP[key] === 'Limited Time' && (similarAdBlockData.lt_start_dt || similarAdBlockData.lt_end_dt)
                              ? `: ${similarAdBlockData.lt_start_dt || '?'} - ${similarAdBlockData.lt_end_dt || '?'}` : ''}
                      </Tag>
                    ) : null;
                  })}
              </Space>
            )}
          </MetadataContainer>
          <Divider orientation="center">Events in Matched Block</Divider>
          <MetadataContainer>
            {similarAdBlockData?.event_nm ? (
              <Space size={8} wrap style={{ display: 'flex', justifyContent: 'center' }}>
                {similarAdBlockData.event_nm.split(',').map((event, index) => (
                  <Tag
                    key={index}
                  >
                    {event}
                  </Tag>
                ))}
              </Space>
            ) : (
              <EmptyContentContainer>
                <p>No Events</p>
              </EmptyContentContainer>
            )}
          </MetadataContainer>

        </Col>
      </Row>

    </Modal>
  );
};

const CompleteMatchButton = styled(Button)`
  background: #c9e75d;
  border: none;
  color: #000;
  &:hover {
    background: #a0d911 !important;
    color: #000 !important;
  }
  &:disabled {
    background: #1a1a1a !important;
    color: #666 !important;
  }
`;

const ReviewQueueButton = styled(Button)`
  background: #ff4d4f;
  border: none;
  color: #fff;
  &:hover {
    background: #ff7875 !important;
    color: #fff !important;
  }
  &:disabled {
    background: #1a1a1a !important;
    color: #666 !important;
  }
`;

const ImageDiv = styled.div`
  width: 450px;
  height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #333;
  border-radius: 8px;
  margin: 16px 0;
  background: #1a1a1a;
  overflow: hidden;
`;

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 450px;
  max-height: 500px;
  object-fit: contain;
  opacity: 1;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    background: #1a1a1a;
    border-color: #333;
    color: #fff;
    
    &:hover {
      color: #fff;
      border-color: #666;
    }
  }
  
  .ant-radio-button-wrapper-checked {
    background: #333 !important;
    border-color: #444 !important;
    color: #fff !important;
    
    &:hover {
      color: #fff !important;
      border-color: #444 !important;
    }
    
    &::before {
      background-color: #444 !important;
    }
  }
`;

const MetadataContainer = styled.div`
  border: 1px solid #333;
  border-radius: 8px;
  padding: 16px;
  min-height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  background: #1a1a1a;
`;

const EmptyContentContainer = styled.div`
  text-align: center;
  width: 100%;
  color: #666;
`;
